// FE-SCRM-049 — Prospect client-side types.
//
// Mirrors the BE-SCRM-003 response shape (see backend/sales/prospect_views.py
// ``_serialize``) plus the BE-SCRM-018 ``fit_score`` columns and the
// FE-SCRM-060 filter/sort URL state.


export type ProspectSource =
  | "referral"
  | "cold_outreach"
  | "inbound"
  | "carrier_referral"
  | "event"
  | "form5500_research"
  | "other";


export type ProspectStatus = "active" | "converted" | "archived";


export type LivesBand =
  | "micro" | "small" | "mid_market" | "large" | "enterprise";


export type RenewalConfidence =
  | "confirmed"
  | "inferred_from_form5500"
  | "producer_estimate"
  | "unknown";


export type BrokerRelationshipStrength = "strong" | "weak" | "unknown";


export interface Prospect {
  prospect_id: string;
  name: string;
  legal_name: string | null;
  normalized_name: string;
  website: string | null;
  ein: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  industry: string | null;
  broker_location_id: string | null;
  estimated_lives: number | null;
  lives_band: LivesBand | null;
  renewal_month: number;
  renewal_year: number;
  renewal_confidence: RenewalConfidence;
  current_broker_firm: string | null;
  current_broker_office: string | null;
  current_broker: string | null;
  current_broker_relationship_strength: BrokerRelationshipStrength;
  source: ProspectSource;
  referred_by_sales_contact_id: string | null;
  referred_by_account_id: string | null;
  referred_by_carrier_contact_id: string | null;
  form5500_sponsor_id: string | null;
  fit_score: number | null;
  fit_reasons: string[];
  fit_score_computed_at: string | null;
  status: ProspectStatus;
  converted_account_id: string | null;
  created_at: string | null;
  updated_at: string | null;
}


export interface ProspectListResponse {
  rows: Prospect[];
  total: number;
}


export interface ProspectCreateInput {
  name: string;
  renewal_month: number;
  renewal_year?: number;
  ein?: string | null;
  estimated_lives?: number | null;
  industry?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  current_broker_firm?: string | null;
  current_broker_office?: string | null;
  current_broker_relationship_strength?: BrokerRelationshipStrength;
  source?: ProspectSource;
  referred_by_account_id?: string | null;
  referred_by_sales_contact_id?: string | null;
  referred_by_carrier_contact_id?: string | null;
  tags?: string[];
}


export type Form5500FilterStatus =
  | "linked"
  | "needs_research"
  | "not_yet_attempted";


export interface ProspectFilters {
  q: string;
  sources: ProspectSource[];
  lives_bands: LivesBand[];
  renewal_month_min: number | null;
  renewal_month_max: number | null;
  fit_score_min: number | null;
  fit_score_max: number | null;
  /** FE-SCRM-063 — single-select Form 5500 link-state facet. ``null``
   *  is the unfiltered default; the URL key is ``form5500_status``. */
  form5500_status: Form5500FilterStatus | null;
}


export type SortKey =
  | "name" | "-name"
  | "renewal" | "-renewal"
  | "fit_score" | "-fit_score"
  | "created_at" | "-created_at";


export const DEFAULT_SORT: SortKey = "renewal";


export const VALID_SOURCES: readonly ProspectSource[] = [
  "referral",
  "cold_outreach",
  "inbound",
  "carrier_referral",
  "event",
  "form5500_research",
  "other",
] as const;


export const VALID_LIVES_BANDS: readonly LivesBand[] = [
  "micro", "small", "mid_market", "large", "enterprise",
] as const;


export const SOURCE_LABEL: Record<ProspectSource, string> = {
  referral: "Referral",
  cold_outreach: "Cold outreach",
  inbound: "Inbound",
  carrier_referral: "Carrier referral",
  event: "Event",
  form5500_research: "Form 5500 research",
  other: "Other",
};


export const LIVES_BAND_LABEL: Record<LivesBand, string> = {
  micro: "Micro (<10)",
  small: "Small (10–50)",
  mid_market: "Mid-market (51–500)",
  large: "Large (501–2,500)",
  enterprise: "Enterprise (2,500+)",
};


export const RELATIONSHIP_STRENGTH_LABEL:
  Record<BrokerRelationshipStrength, string> = {
    strong: "Strong",
    weak: "Weak",
    unknown: "Unknown",
  };


export const MONTH_LABEL: Record<number, string> = {
  1: "January", 2: "February", 3: "March", 4: "April",
  5: "May", 6: "June", 7: "July", 8: "August",
  9: "September", 10: "October", 11: "November", 12: "December",
};


export const EMPTY_FILTERS: ProspectFilters = {
  q: "",
  sources: [],
  lives_bands: [],
  renewal_month_min: null,
  renewal_month_max: null,
  fit_score_min: null,
  fit_score_max: null,
  form5500_status: null,
};


export const VALID_FORM5500_STATUS: readonly Form5500FilterStatus[] = [
  "linked",
  "needs_research",
  "not_yet_attempted",
] as const;


export const FORM5500_STATUS_LABEL: Record<Form5500FilterStatus, string> = {
  linked: "Linked",
  needs_research: "Needs research",
  not_yet_attempted: "Not yet attempted",
};


export interface CrmFormBannerErrorMap {
  [field: string]: string;
}
